/**
 * Route-level page metadata, attached to a <Route> via its `handle` prop and
 * read back through `useMatches()`.
 *
 * This is the single source of truth for a page's title: the same entry feeds
 * the TopBar heading and the document (browser tab) title, so the two cannot
 * drift apart the way the four separate title tables used to.
 */
/**
 * One ancestor of a page: a word for the browser tab, and optionally the route
 * it names.
 *
 * A bare string is the default and stays the common case; most segments name a
 * section rather than a page ("Admin"), and a link to nowhere is worse than no
 * link. The object form is for an ancestor that IS a page, where the trail is
 * the way back: a session detail is reached from the Enrichment table, and
 * leaving that crumb inert makes the reader hunt the side nav for a screen the
 * breadcrumb is already pointing at.
 */
export type TabSegment = string | { label: string; to: string; crumbOnly?: true };

/** The words of a segment, whichever form it took. */
export function segmentLabel(segment: TabSegment): string {
  return typeof segment === 'string' ? segment : segment.label;
}

/**
 * Whether a segment reaches the browser tab. `crumbOnly` keeps a root that
 * every trail shares (the client Dashboard) out of the tab: there it would only
 * repeat itself on every page and push the brand out of sight.
 */
export function isInTabTitle(segment: TabSegment): boolean {
  return typeof segment === 'string' || !segment.crumbOnly;
}

export interface RouteHandle {
  /** The TopBar heading, and the first segment of the document title. */
  title: string;
  /**
   * Extra document-title segments appended after `title`, most specific first.
   * They give the browser tab context the on-screen heading does not need:
   * `['Lists', 'Admin']` yields "List Details | Lists | Admin | Invyo".
   *
   * A segment may carry the route it names (see `TabSegment`), which turns that
   * crumb into a link in the TopBar. The document title is unaffected either way.
   */
  tabSegments?: readonly TabSegment[];
  /**
   * Render the heading in the page instead of the TopBar.
   *
   * When set, the TopBar demotes the title to the last (emphasised) breadcrumb
   * (the trail stays complete, only the large heading moves) and the page is
   * expected to mount `<PageTitle />`. Opt-in per route, because the TopBar's h1
   * is the only place every route's title is rendered: flipping it for everyone
   * would leave 50 pages without a heading until each grew its own.
   */
  titleInPage?: boolean;
}

/**
 * React Router types `handle` as `unknown`, so it has to be narrowed before use.
 */
export function isRouteHandle(handle: unknown): handle is RouteHandle {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    'title' in handle &&
    typeof (handle as { title: unknown }).title === 'string'
  );
}
